import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Send, Search, Users, Briefcase, MapPin, Calendar,
  CheckCircle, XCircle, Clock, User, ChevronRight, X,
  GraduationCap, DollarSign, AlertTriangle, Eye, Star, Phone, Edit2, ShieldAlert,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Job, SalaryRange } from '../types'

// ─── Types ────────────────────────────────────────────────────
type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired'

interface SentInvitation {
  id: string
  job_id: string
  alumni_id: string
  employer_id: string
  message?: string
  status: InvitationStatus
  invited_at: string
  responded_at?: string
  job_title: string
  employment_type?: string
  experience_level?: string
  job_location?: string
  job_salary_range?: string
  company_name: string
  alumni_first_name?: string
  alumni_last_name?: string
  alumni_program?: string
  alumni_department?: string
  alumni_year_graduated?: number
  alumni_profile_picture?: string
}

// ─── Helpers ──────────────────────────────────────────────────
const TYPE_LABELS: Record<string, string> = {
  full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract',
  internship: 'Internship', freelance: 'Freelance',
}

const LEVEL_LABELS: Record<string, string> = {
  entry: 'Entry Level', mid: 'Mid Level', senior: 'Senior Level', executive: 'Executive',
}

const SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under ₱20,000', '20k_40k': '₱20,000 – ₱40,000',
  '40k_60k': '₱40,000 – ₱60,000', '60k_80k': '₱60,000 – ₱80,000',
  '80k_100k': '₱80,000 – ₱100,000', over_100k: 'Over ₱100,000',
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function alumniInitials(inv: SentInvitation) {
  const f = inv.alumni_first_name?.charAt(0) ?? ''
  const l = inv.alumni_last_name?.charAt(0) ?? ''
  return (f + l).toUpperCase() || '?'
}

function alumniFullName(inv: SentInvitation) {
  const name = `${inv.alumni_first_name ?? ''} ${inv.alumni_last_name ?? ''}`.trim()
  return name || 'Unknown Alumni'
}

// ─── Status config ────────────────────────────────────────────
const STATUS_CONFIG: Record<InvitationStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending:  { label: 'Pending',          color: 'text-yellow-700', bg: 'bg-yellow-100', icon: <Clock         className="w-3.5 h-3.5" /> },
  accepted: { label: 'Accepted',         color: 'text-green-700',  bg: 'bg-green-100',  icon: <CheckCircle   className="w-3.5 h-3.5" /> },
  declined: { label: 'Declined',         color: 'text-red-700',    bg: 'bg-red-100',    icon: <XCircle       className="w-3.5 h-3.5" /> },
  expired:  { label: 'Closed by Admin',  color: 'text-gray-600',   bg: 'bg-gray-100',   icon: <ShieldAlert   className="w-3.5 h-3.5" /> },
}

// ─── Revoke Confirm Modal ─────────────────────────────────────
interface RevokeConfirmProps {
  invitation: SentInvitation
  onConfirm: () => void
  onCancel: () => void
  loading: boolean
}

const RevokeConfirmModal: React.FC<RevokeConfirmProps> = ({ invitation, onConfirm, onCancel, loading }) =>
  createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Revoke Invitation?</h3>
              <p className="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            You are about to revoke the invitation sent to{' '}
            <strong>{alumniFullName(invitation)}</strong> for{' '}
            <strong>{invitation.job_title}</strong>. The alumni will no longer be able to respond to this invitation.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-50"
            >
              {loading ? 'Revoking…' : 'Yes, Revoke'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )

// ─── Main Page ────────────────────────────────────────────────
const EmployerInvitationsPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [invitations, setInvitations] = useState<SentInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | InvitationStatus>('all')
  const [search, setSearch] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<SentInvitation | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [viewingJobDetail, setViewingJobDetail] = useState<Job | null>(null)
  const [loadingJob, setLoadingJob] = useState(false)
  const [viewingAlumniInv, setViewingAlumniInv] = useState<SentInvitation | null>(null)
  const [alumniProfileData, setAlumniProfileData] = useState<Record<string, any> | null>(null)
  const [alumniProfileLoading, setAlumniProfileLoading] = useState(false)
  const [jobStats, setJobStats] = useState({ total: 0, active: 0, applications: 0 })

  // ── Fetch ──────────────────────────────────────────────────
  const fetchInvitations = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabaseClient.rpc('get_sent_invitations')
      if (error) throw error
      setInvitations((data as SentInvitation[]) || [])
    } catch (err) {
      console.error('Failed to load sent invitations:', err)
      toast.error('Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const fetchJobStats = useCallback(async () => {
    try {
      const { data } = await supabaseClient.rpc('get_employer_jobs')
      const jobs = (data || []) as any[]
      setJobStats({
        total: jobs.length,
        active: jobs.filter(j => j.status === 'active').length,
        applications: jobs.reduce((s, j) => s + (j.application_count || 0), 0),
      })
    } catch { /* non-critical */ }
  }, [])

  useEffect(() => { fetchInvitations(); fetchJobStats() }, [fetchInvitations, fetchJobStats])

  // ── Revoke ─────────────────────────────────────────────────
  const handleRevoke = async () => {
    if (!revokeTarget) return
    setRevoking(true)
    try {
      const { data, error } = await supabaseClient.rpc('revoke_invitation', {
        p_invitation_id: revokeTarget.id,
      })
      if (error) throw error
      const result = data as { success: boolean; message: string }
      if (!result.success) {
        toast.error(result.message)
        return
      }
      toast.success('Invitation revoked')
      setInvitations(prev => prev.filter(i => i.id !== revokeTarget.id))
      setRevokeTarget(null)
    } catch (err) {
      console.error('Failed to revoke invitation:', err)
      toast.error('Failed to revoke invitation')
    } finally {
      setRevoking(false)
    }
  }

  // ── View Job Detail ────────────────────────────────────────
  const openJobDetail = async (jobId: string) => {
    setLoadingJob(true)
    try {
      const { data, error } = await supabaseClient
        .from('jobs')
        .select('*, companies(name)')
        .eq('id', jobId)
        .single()
      if (error) throw error
      const job: Job = {
        ...data,
        company_name: (data.companies as any)?.name ?? '',
      }
      setViewingJobDetail(job)
    } catch (err) {
      console.error('Failed to load job details:', err)
      toast.error('Failed to load job details')
    } finally {
      setLoadingJob(false)
    }
  }

  // ── View Alumni Profile ──────────────────────────────────
  const openAlumniProfile = async (inv: SentInvitation) => {
    setViewingAlumniInv(inv)
    setAlumniProfileLoading(true)
    try {
      const { data } = await supabaseClient
        .from('alumni_profiles')
        .select('first_name, last_name, program, department, year_graduated, skills_acquired, profile_picture, phone, current_employment_status, field_of_work, job_position')
        .eq('user_id', inv.alumni_id)
        .single()
      setAlumniProfileData(data || {})
    } catch {
      setAlumniProfileData(null)
    } finally {
      setAlumniProfileLoading(false)
    }
  }

  // ── Derived ────────────────────────────────────────────────
  const counts = {
    all:      invitations.length,
    pending:  invitations.filter(i => i.status === 'pending').length,
    accepted: invitations.filter(i => i.status === 'accepted').length,
    declined: invitations.filter(i => i.status === 'declined').length,
    expired:  invitations.filter(i => i.status === 'expired').length,
  }

  const filtered = invitations.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        alumniFullName(inv).toLowerCase().includes(q) ||
        inv.job_title.toLowerCase().includes(q) ||
        (inv.alumni_program ?? '').toLowerCase().includes(q) ||
        (inv.alumni_department ?? '').toLowerCase().includes(q)
      )
    }
    return true
  })

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold">Sent Invitations</h1>
          <p className="text-blue-200 text-sm mt-1">
            Track and manage all the invitations you've sent to alumni candidates
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5 text-yellow-300" />
              {counts.all} invitation{counts.all !== 1 ? 's' : ''} sent
            </span>
            {counts.pending > 0 && (
              <span className="bg-yellow-500/20 border border-yellow-400/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-yellow-300" />
                {counts.pending} pending
              </span>
            )}
            {counts.accepted > 0 && (
              <span className="bg-yellow-400/20 border border-yellow-300/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-yellow-300" />
                {counts.accepted} accepted
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-blue-900 px-4 py-2.5 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-white/80" />
            <span className="text-xs font-medium text-white/90">Total Jobs</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{jobStats.total}</p>
            <p className="text-xs text-gray-400 mt-0.5">All job postings</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-yellow-400 px-4 py-2.5 flex items-center gap-2">
            <Send className="w-4 h-4 text-gray-800" />
            <span className="text-xs font-medium text-gray-800">Active</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{jobStats.active}</p>
            <p className="text-xs text-gray-400 mt-0.5">Currently live</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-blue-900 px-4 py-2.5 flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-white/80" />
            <span className="text-xs font-medium text-white/90">Invitations Sent</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{counts.all}</p>
            <p className="text-xs text-gray-400 mt-0.5">Total sent</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-yellow-400 px-4 py-2.5 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-800" />
            <span className="text-xs font-medium text-gray-800">Applications</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{jobStats.applications}</p>
            <p className="text-xs text-gray-400 mt-0.5">Total received</p>
          </div>
        </div>
      </div>

      {/* ── Tab Switcher ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => navigate('/employer/jobs')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition text-gray-600 hover:text-gray-900"
        >
          <Briefcase className="w-4 h-4" /> All Job Listings
        </button>
        <button
          onClick={() => navigate('/employer/applications')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition text-gray-600 hover:text-gray-900"
        >
          <Users className="w-4 h-4" /> All Applications
        </button>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition bg-white shadow-sm text-gray-900"
        >
          <Send className="w-4 h-4" /> Invitations Sent
        </button>
      </div>

      {/* ── Search ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by alumni name, job title, or program..."
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* ── Status filter tabs ── */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'pending', 'accepted', 'declined', 'expired'] as const).map(s => {
          const count = counts[s]
          const isActive = statusFilter === s
          const badgeColors: Record<string, string> = {
            all:      isActive ? 'bg-blue-900 text-white'    : 'bg-gray-200 text-gray-600',
            pending:  isActive ? 'bg-yellow-500 text-white'  : 'bg-yellow-100 text-yellow-700',
            accepted: isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700',
            declined: isActive ? 'bg-red-500 text-white'     : 'bg-red-100 text-red-600',
            expired:  isActive ? 'bg-gray-600 text-white'    : 'bg-gray-100 text-gray-500',
          }
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
                isActive
                  ? 'bg-white border-gray-900 text-gray-900 shadow-sm'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center ${badgeColors[s]}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
        </div>
      ) : invitations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-14 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No invitations sent yet</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">
            Use the candidate matching tool to find qualified alumni and send them job invitations.
          </p>
          <button
            onClick={() => navigate('/employer/candidates')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white text-sm font-semibold rounded-xl hover:bg-blue-800 transition"
          >
            <Users className="w-4 h-4" /> Find Candidates
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-700">No matching invitations</h3>
          <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(inv => {
            const cfg = STATUS_CONFIG[inv.status]
            const isPending  = inv.status === 'pending'
            const isAccepted = inv.status === 'accepted'

            return (
              <div
                key={inv.id}
                className={`bg-white rounded-2xl border shadow-sm transition ${
                  isPending ? 'border-yellow-200' : 'border-gray-200'
                }`}
              >
                <div className="p-5">
                  {/* ── Top row: alumni avatar + name + status ── */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Alumni avatar */}
                      {inv.alumni_profile_picture ? (
                        <img
                          src={inv.alumni_profile_picture}
                          alt=""
                          className="w-11 h-11 rounded-xl object-cover flex-shrink-0 border border-gray-100"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-xl bg-blue-900 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {alumniInitials(inv)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-900 leading-snug">{alumniFullName(inv)}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[inv.alumni_program, inv.alumni_department].filter(Boolean).join(' · ')}
                          {inv.alumni_year_graduated ? ` · Class of ${inv.alumni_year_graduated}` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Status badge */}
                    <span className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold rounded-full ${cfg.bg} ${cfg.color}`}>
                      {cfg.icon} {cfg.label}
                    </span>
                  </div>

                  {/* ── Job row ── */}
                  <div className="flex items-center gap-2 mb-3 p-3 bg-gray-50 rounded-xl">
                    <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800 truncate">{inv.job_title}</p>
                      <p className="text-xs text-gray-500">{inv.company_name}</p>
                    </div>
                    <button
                      onClick={() => openJobDetail(inv.job_id)}
                      disabled={loadingJob}
                      className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-700 font-medium hover:text-blue-800 transition"
                    >
                      <Eye className="w-3.5 h-3.5" /> View job
                    </button>
                  </div>

                  {/* ── Job meta chips ── */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-3">
                    {inv.employment_type && (
                      <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        <Briefcase className="w-3 h-3" />
                        {TYPE_LABELS[inv.employment_type] || inv.employment_type}
                      </span>
                    )}
                    {inv.job_location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {inv.job_location}
                      </span>
                    )}
                    {inv.job_salary_range && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> {SALARY_LABELS[inv.job_salary_range] || inv.job_salary_range}
                      </span>
                    )}
                    {inv.alumni_program && (
                      <span className="flex items-center gap-1">
                        <GraduationCap className="w-3 h-3" /> {inv.alumni_program}
                      </span>
                    )}
                  </div>

                  {/* ── Personal message ── */}
                  {inv.message && (
                    <div className="bg-blue-50 border-l-4 border-blue-300 rounded-r-lg px-4 py-2.5 mb-3">
                      <p className="text-[11px] font-semibold text-blue-500 uppercase tracking-wide mb-1">Your message</p>
                      <p className="text-sm text-blue-800 italic">"{inv.message}"</p>
                    </div>
                  )}

                  {/* ── Footer: dates + actions ── */}
                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400">
                      <Calendar className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                      Sent {formatDate(inv.invited_at)}
                      {inv.responded_at && ` · Responded ${formatDate(inv.responded_at)}`}
                    </p>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openAlumniProfile(inv)}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition"
                      >
                        <User className="w-3.5 h-3.5" /> View Profile
                      </button>
                      {isPending && (
                        <button
                          onClick={() => setRevokeTarget(inv)}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 border border-red-200 text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 transition"
                        >
                          <X className="w-3.5 h-3.5" /> Revoke
                        </button>
                      )}
                      {isAccepted && (
                        <button
                          onClick={() => navigate('/employer/applications')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-100 text-blue-800 text-sm font-semibold rounded-xl hover:bg-blue-200 transition"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> View Application
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Job Detail Modal ── */}
      {viewingJobDetail && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewingJobDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-bold shrink-0">
                  {viewingJobDetail.title.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{viewingJobDetail.title}</h2>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      viewingJobDetail.status === 'active' ? 'bg-green-100 text-green-700'
                      : viewingJobDetail.status === 'closed' ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                    }`}>
                      {viewingJobDetail.status.toUpperCase()}
                    </span>
                    {viewingJobDetail.is_partner_listing && (
                      <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                        <Star className="w-3 h-3" /> PARTNER
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{viewingJobDetail.company_name}</p>
                </div>
              </div>
              <button onClick={() => setViewingJobDetail(null)} className="p-1 hover:bg-gray-100 rounded-lg shrink-0"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Key info pills */}
              <div className="flex flex-wrap gap-2 text-sm">
                {viewingJobDetail.employment_type && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    <Briefcase className="w-3.5 h-3.5 text-gray-400" /> {TYPE_LABELS[viewingJobDetail.employment_type] || viewingJobDetail.employment_type}
                  </span>
                )}
                {viewingJobDetail.experience_level && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    {LEVEL_LABELS[viewingJobDetail.experience_level] || viewingJobDetail.experience_level}
                  </span>
                )}
                {viewingJobDetail.location && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" /> {viewingJobDetail.location}
                  </span>
                )}
                {viewingJobDetail.salary_range && (
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                    <DollarSign className="w-3.5 h-3.5" /> {SALARY_LABELS[viewingJobDetail.salary_range] || viewingJobDetail.salary_range}
                  </span>
                )}
                {viewingJobDetail.deadline && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium ${new Date(viewingJobDetail.deadline) < new Date() ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'}`}>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(viewingJobDetail.deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(viewingJobDetail.deadline)}
                  </span>
                )}
              </div>

              {/* Description */}
              {viewingJobDetail.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{viewingJobDetail.description}</p>
                </div>
              )}

              {/* Requirements */}
              {(viewingJobDetail.requirements?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Requirements</h3>
                  <ul className="space-y-1">
                    {viewingJobDetail.requirements.map((r: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills */}
              {(viewingJobDetail.skills_required?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Skills Required</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {viewingJobDetail.skills_required.map((s: string) => (
                      <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{viewingJobDetail.application_count || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Posted</p>
                  <p className="text-sm font-semibold text-gray-700 mt-1">{formatDate(viewingJobDetail.posted_at)}</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setViewingJobDetail(null)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Alumni Profile Modal ── */}
      {viewingAlumniInv && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setViewingAlumniInv(null); setAlumniProfileData(null) }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Alumni Profile</h3>
              <button onClick={() => { setViewingAlumniInv(null); setAlumniProfileData(null) }} className="p-1 rounded-lg hover:bg-gray-100 transition">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {alumniProfileLoading ? (
              <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : !alumniProfileData ? (
              <div className="p-8 text-center text-gray-500">Profile not found</div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">

                {/* Avatar + identity */}
                <div className="flex items-center gap-4">
                  {viewingAlumniInv.alumni_profile_picture ? (
                    <img src={viewingAlumniInv.alumni_profile_picture} alt="" className="w-16 h-16 rounded-2xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-blue-900 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                      {(alumniProfileData.first_name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-gray-900 leading-snug">
                      {alumniProfileData.first_name} {alumniProfileData.last_name}
                    </p>
                    {alumniProfileData.program && <p className="text-sm text-gray-500 mt-0.5">{alumniProfileData.program}</p>}
                    {alumniProfileData.department && <p className="text-xs text-gray-400">{alumniProfileData.department}</p>}
                  </div>
                </div>

                {/* Contact */}
                {alumniProfileData.phone && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Contact</p>
                    <div className="flex items-center gap-2.5 text-sm text-gray-700">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      {alumniProfileData.phone}
                    </div>
                  </div>
                )}

                {/* Education */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Education</p>
                  <div className="grid grid-cols-2 gap-2">
                    {alumniProfileData.program && (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">Program</p>
                        <p className="text-sm font-semibold text-gray-800">{alumniProfileData.program}</p>
                      </div>
                    )}
                    {alumniProfileData.year_graduated && (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">Graduated</p>
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                          <GraduationCap className="w-3.5 h-3.5 text-gray-400" /> Class of {alumniProfileData.year_graduated}
                        </p>
                      </div>
                    )}
                    {alumniProfileData.department && (
                      <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                        <p className="text-xs text-gray-400 mb-0.5">Department</p>
                        <p className="text-sm font-semibold text-gray-800">{alumniProfileData.department}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Career */}
                {(alumniProfileData.current_employment_status || alumniProfileData.job_position || alumniProfileData.field_of_work) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Career</p>
                    <div className="grid grid-cols-2 gap-2">
                      {alumniProfileData.current_employment_status && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Employment Status</p>
                          <p className="text-sm font-semibold text-gray-800 capitalize">
                            {alumniProfileData.current_employment_status.replace(/_/g, ' ')}
                          </p>
                        </div>
                      )}
                      {alumniProfileData.job_position && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Current Position</p>
                          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                            <Briefcase className="w-3.5 h-3.5 text-gray-400" /> {alumniProfileData.job_position}
                          </p>
                        </div>
                      )}
                      {alumniProfileData.field_of_work && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Field of Work</p>
                          <p className="text-sm font-semibold text-gray-800">{alumniProfileData.field_of_work}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {(alumniProfileData.skills_acquired?.length > 0) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {alumniProfileData.skills_acquired.map((s: string) => (
                        <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={() => { setViewingAlumniInv(null); setAlumniProfileData(null) }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Revoke confirm modal ── */}
      {revokeTarget && (
        <RevokeConfirmModal
          invitation={revokeTarget}
          onConfirm={handleRevoke}
          onCancel={() => setRevokeTarget(null)}
          loading={revoking}
        />
      )}
    </div>
  )
}

export default EmployerInvitationsPage
